import React from "react";
import { Controller, Control, FieldError } from "react-hook-form";

interface PhoneNumberInputProps {
  control: Control<any>;
  name: string;
  label?: string;
  error?: FieldError;
  placeholder?: string;
  className?: string;
  required?: boolean;
  labelClassName?: string;
  gap?: string;
}

/**
 * Formats phone number as user types to match mobile app format: (XXX) XXX-XXXX
 * Only allows digits, limits to 10 digits
 * Matches Flutter PhoneNumberFormatter behavior exactly
 */
const formatPhoneNumber = (value: string) => {
  // Remove all non-digits
  let digitsOnly = value.replace(/\D/g, "");

  // Limit to 10 digits (matching mobile app behavior)
  if (digitsOnly.length > 10) {
    digitsOnly = digitsOnly.substring(0, 10);
  }

  // Format the digits into (XXX) XXX-XXXX
  // This matches the Flutter PhoneNumberFormatter logic exactly
  let formattedText = "";

  if (digitsOnly.length > 0) {
    formattedText += "(";
  }

  if (digitsOnly.length > 3) {
    formattedText += digitsOnly.substring(0, 3) + ") ";
  } else {
    formattedText += digitsOnly;
  }

  if (digitsOnly.length > 6) {
    formattedText += digitsOnly.substring(3, 6) + "-";
  } else if (digitsOnly.length > 3) {
    formattedText += digitsOnly.substring(3);
  }

  if (digitsOnly.length > 6) {
    formattedText += digitsOnly.substring(6);
  }

  return formattedText;
};

/**
 * Validates phone number to match mobile app validation exactly
 * Returns error message string if invalid, true if valid
 * Matches Flutter validatePhoneNumber behavior exactly
 */
const validatePhoneNumber = (
  value: string | null | undefined
): string | true => {
  if (value == null || value === "" || value.trim() === "") {
    return "Phone Number cannot be empty";
  }

  // Remove all non-digits for validation
  const cleanedNumber = value.replace(/\D/g, "");

  if (cleanedNumber.length !== 10) {
    return "Phone number must be 10 digits";
  }

  return true; // Return true if validation is successful (matching mobile app's null return)
};

const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
  control,
  name,
  label = "Phone Number",
  error,
  placeholder = "(123) 456-7890",
  className = "",
  required = false,
  labelClassName = "text-labelTextColor text-base font-semibold",
  gap = "gap-1",
}) => {
  return (
    <div className={`flex flex-col ${gap}`}>
      {label && <label className={labelClassName}>{label}</label>}

      <Controller
        name={name}
        control={control}
        rules={{
          required: required ? "Phone Number is required" : false,
          validate: validatePhoneNumber,
        }}
        render={({ field: { onChange, value, onBlur } }) => (
          <input
            type="text"
            value={value || ""}
            onChange={(e) => {
              const formatted = formatPhoneNumber(e.target.value);
              onChange(formatted);
            }}
            onBlur={(e) => {
              // Trim the value on blur (matching mobile app behavior)
              const trimmed = (e.target.value || "").trim();
              if (trimmed !== value) {
                onChange(trimmed);
              }
              onBlur();
            }}
            placeholder={placeholder}
            className={className || "custom-input"}
          />
        )}
      />

      {error && <p className="text-red-500 text-sm">{error.message}</p>}
    </div>
  );
};

export default PhoneNumberInput;
